"use client";

import Image from "next/image";
import { useState } from "react";

import { CcShowcaseHeightShell } from "@/components/services/shared/CcShowcaseHeightShell";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import { CcAiAnswer } from "./CcAiAnswer";
import type { AiValueDemoCopy } from "./ai-value-demo-copy";

type CcAiShowcaseProps = {
  copy: AiValueDemoCopy["showcase"];
};

/**
 * Before/after of AI answers about one made-up company: text and button left,
 * both answer cards visible at once on the right (stacked on mobile). Pills
 * switch AI service only.
 */
export function CcAiShowcase({ copy }: CcAiShowcaseProps) {
  const [activeId, setActiveId] = useState(copy.cases[0]?.id ?? "");

  const active = copy.cases.find((item) => item.id === activeId) ?? copy.cases[0];
  if (!active) return null;

  const labels = {
    question: copy.questionLabel,
    answer: copy.answerLabel,
    sources: copy.sourcesLabel,
  };

  return (
    <section
      className="cc-section cc-web-showcase-section cc-ai-showcase-section"
      aria-labelledby="cc-ai-showcase-title"
      data-cc-nav="light"
      {...devSectionAttr("ai-showcase")}
    >
      <div className="cc-container">
        <h2 id="cc-ai-showcase-title" className="cc-web-showcase__sr">
          {copy.sectionTitle}
        </h2>
        <div className="cc-web-showcase">
          <div className="cc-web-showcase__intro">
            {/* All cases share one grid cell, so the column is as tall as the longest text. */}
            <div className="cc-web-showcase__copies">
              {copy.cases.map((item) => {
                const isActive = item.id === active.id;
                return (
                  <div
                    key={item.id}
                    className="cc-web-showcase__copy"
                    data-active={isActive}
                    aria-hidden={!isActive}
                    inert={!isActive}
                  >
                    <CcSanjayaEyebrow theme="on-subtle">{item.name}</CcSanjayaEyebrow>
                    <h3 className="cc-web-showcase__title">{item.title}</h3>
                    <p className="cc-web-showcase__text">{item.text}</p>
                    <ul className="cc-web-showcase__points">
                      {item.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <CcButton
              bookCallPopup
              bookCallVariant="default"
              label={copy.button}
              expand
              expandCompact
              className="cc-web-showcase__button"
            />
          </div>

          <figure className="cc-web-showcase__media">
            <div
              className="cc-web-showcase__switch"
              role="group"
              aria-label={copy.tablistLabel}
            >
              {copy.cases.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="cc-web-showcase__pill"
                  aria-pressed={item.id === active.id}
                  onClick={() => setActiveId(item.id)}
                >
                  <Image
                    className="cc-ai-showcase__pill-logo"
                    src={item.logo}
                    alt=""
                    width={14}
                    height={14}
                    unoptimized
                  />
                  {item.name}
                </button>
              ))}
            </div>

            {/* All cases share one grid cell so height stays at the tallest tab. */}
            <CcShowcaseHeightShell
              shellClassName="cc-ai-showcase__answers-shell"
              innerClassName="cc-ai-showcase__answers"
              remeasureKey={activeId}
            >
              {copy.cases.map((item) => {
                const isActive = item.id === active.id;
                return (
                  <div
                    key={item.id}
                    className="cc-ai-showcase__answers-case"
                    data-active={isActive}
                    aria-hidden={!isActive}
                    inert={!isActive}
                  >
                    <div className="cc-ai-showcase__answer-panel">
                      <CcAiAnswer
                        service={item.name}
                        logo={item.logo}
                        stateLabel={copy.beforeLabel}
                        question={item.question}
                        answer={item.before}
                        highlight={copy.highlight}
                        labels={labels}
                      />
                    </div>
                    <div className="cc-ai-showcase__answer-panel">
                      <CcAiAnswer
                        service={item.name}
                        logo={item.logo}
                        stateLabel={copy.afterLabel}
                        question={item.question}
                        answer={item.after}
                        highlight={copy.highlight}
                        labels={labels}
                      />
                    </div>
                  </div>
                );
              })}
            </CcShowcaseHeightShell>
            <figcaption className="cc-web-showcase__hint">{copy.hint}</figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}
